import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DEFAULT_MODEL_PROPS } from "../constants/defaults";
import { generateUUID } from "../utils/uuid";
import { createDebouncedStorage } from "./helpers";
import { useHistoryStore } from "./historyStore";

// Track current room ID to prevent stale localStorage hydration
let currentRoomId = null;

export const usePlacedModelsStore = create(
  persist(
    (set, get) => ({
      placedModels: [],
      _roomId: null, // Store the room ID this data belongs to

      getModelById: (id) => {
        return get().placedModels.find((model) => model.id === id);
      },

      // Set the current room ID when loading a new room
      setRoomId: (roomId) => {
        currentRoomId = roomId;
        set({ _roomId: roomId });
      },

      addModel: ({
        url,
        name,
        position = [...DEFAULT_MODEL_PROPS.position],
        rotation = [...DEFAULT_MODEL_PROPS.rotation],
        scale = [...DEFAULT_MODEL_PROPS.scale],
        hue,
        assetId = null,
        category = null,
        roughness = DEFAULT_MODEL_PROPS.roughness,
        type = null,
        avatarConfig = null,
        avatarId = null,
      }) => {
        const id = generateUUID();
        useHistoryStore.getState().push(get().placedModels);
        set((state) => ({
          placedModels: [
            ...state.placedModels,
            {
              id,
              name,
              url,
              position,
              rotation,
              scale,
              hue,
              roughness,
              assetId,
              category,
              type,
              avatarConfig,
              avatarId,
              music: null,
              musicAssetKey: null,
              link: null,
              hoverText: null,
            },
          ],
        }));
        return id;
      },

      updateTransform: (id, transform, skipHistory = false) => {
        if (!skipHistory) useHistoryStore.getState().push(get().placedModels);
        set((state) => ({
          placedModels: state.placedModels.map((model) =>
            model.id === id ? { ...model, ...transform } : model
          ),
        }));
      },

      updateName: (id, newName) => {
        useHistoryStore.getState().push(get().placedModels);
        set((state) => ({
          placedModels: state.placedModels.map((model) =>
            model.id === id ? { ...model, name: newName } : model
          ),
        }));
      },
      updateMusic: (id, newMusic, newMusicAssetKey = null) => {
        useHistoryStore.getState().push(get().placedModels);
        set((state) => ({
          placedModels: state.placedModels.map((model) =>
            model.id === id
              ? {
                  ...model,
                  music: newMusic,
                  musicAssetKey: newMusicAssetKey,
                  ...(newMusic ? { link: null, hoverText: null } : {}),
                }
              : model
          ),
        }));
      },
      updateMusicLoop: (id, loop) => {
        set((state) => ({
          placedModels: state.placedModels.map((model) =>
            model.id === id ? { ...model, musicLoop: loop } : model
          ),
        }));
      },
      updateLink: (id, newLink) => {
        useHistoryStore.getState().push(get().placedModels);
        set((state) => ({
          placedModels: state.placedModels.map((model) =>
            model.id === id
              ? {
                  ...model,
                  link: newLink,
                  ...(newLink ? { music: null, musicAssetKey: null } : {}),
                }
              : model
          ),
        }));
      },
      updateHoverText: (id, newHoverText) => {
        useHistoryStore.getState().push(get().placedModels);
        set((state) => ({
          placedModels: state.placedModels.map((model) =>
            model.id === id ? { ...model, hoverText: newHoverText } : model
          ),
        }));
      },
      updateHue: (id, hue, updateTheHistory) => {
        if (updateTheHistory)
          useHistoryStore.getState().push(get().placedModels);
        set((state) => ({
          placedModels: state.placedModels.map((model) =>
            model.id === id ? { ...model, hue } : model
          ),
        }));
      },
      updateRoughness: (id, roughness, updateTheHistory) => {
        if (updateTheHistory)
          useHistoryStore.getState().push(get().placedModels);
        set((state) => ({
          placedModels: state.placedModels.map((model) =>
            model.id === id ? { ...model, roughness } : model
          ),
        }));
      },

      removeModel: (id) => {
        useHistoryStore.getState().push(get().placedModels);
        set((state) => ({
          placedModels: state.placedModels.filter((model) => model.id !== id),
        }));
      },

      clearAll: () => {
        useHistoryStore.getState().push(get().placedModels);
        set({ placedModels: [] });
      },
    }),
    {
      name: "placed-models-store", // unique name for localStorage key
      // Debounced storage to prevent UI jank during rapid updates
      storage: createDebouncedStorage(localStorage, 500),
      // Only persist the placedModels array along with room ID
      partialize: (state) => ({
        placedModels: state.placedModels,
        _roomId: state._roomId,
      }),
      // Custom hydration to prevent loading stale data from different rooms
      onRehydrateStorage: () => (state, _error, _version) => {
        // Only restore if the stored room ID matches the current room ID
        // This prevents stale localStorage data from being hydrated when switching rooms
        if (state && state._roomId && currentRoomId && state._roomId !== currentRoomId) {
          // Clear stale data if it's from a different room
          console.log(`⚠️ Ignoring stale placedModels from room ${state._roomId}, expected room ${currentRoomId}`);
          state.placedModels = [];
        }
      },
    }
  )
);
